import { useEffect, useRef, useState } from 'react';

/**
 * The text an option holding math is named by.
 *
 * Accessible names take no text from MathML: axe counts it as embedded
 * content, so an option that was only an equation had no name at all, and one
 * that mixed text and math was named by its text alone. KaTeX keeps each
 * equation's TeX beside its MathML, the usual text alternative for math, so
 * the name reads the option's text with each equation as its TeX. Returns null
 * when the element holds no math, so its own text names it as before.
 */
export function getMathTextAlternative(element: Element | null): string | null {
  if (!element?.querySelector('.katex')) {
    return null;
  }

  const parts: string[] = [];
  const read = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent ?? '');
      return;
    }

    if (!(node instanceof Element) || node.getAttribute('aria-hidden') === 'true') {
      return;
    }

    if (node.classList.contains('katex')) {
      const tex = node.querySelector('annotation[encoding="application/x-tex"]');
      parts.push(` ${tex?.textContent ?? ''} `);
      return;
    }

    if (node instanceof HTMLImageElement) {
      parts.push(` ${node.alt} `);
      return;
    }

    node.childNodes.forEach(read);
  };

  read(element);

  const text = parts.join('').replace(/\s+/g, ' ').trim();
  return text.length > 0 ? text : null;
}

/** The text alternative of what `ref` renders, read again whenever `content` changes. */
export function useMathTextAlternative<T extends Element>(content: unknown) {
  const ref = useRef<T | null>(null);
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    setText(getMathTextAlternative(ref.current));
  }, [content]);

  return { ref, text };
}
